import { graphql } from '../../gql';
import { useApiMutation, useApiQuery } from '../../query/hooks';
import { Typography } from '@mui/material';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';
import { ModalContent } from '../../modal/components/ModalContent';
import { ModalActions } from '../../modal/components/ModalActions';
import { useModalContext } from '../../modal/ModalContext';
import { useNavigate } from 'react-router-dom';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { useSnackbar } from '../../store/snackbar';

const actionDeleteModal_getAction = graphql(`
	query actionDeleteModal_getAction($id: UUID!) {
		getAction(by: { id: $id }) {
			name
		}
	}
`);

const actionDeleteModal_deleteAction = graphql(`
	mutation actionDeleteModal_deleteAction($id: UUID!) {
		deleteAction(by: { id: $id }) {
			ok
			errorMessage
		}
	}
`);

export const deleteActionModalSchema = z.object({
	id: z.string(),
});

export const DeleteActionModal: FC<z.infer<typeof deleteActionModalSchema>> = ({ id }) => {
	const { mutate: deleteAction } = useApiMutation(actionDeleteModal_deleteAction);
	const { data } = useApiQuery(actionDeleteModal_getAction, { variables: { id } });
	const { closeModal } = useModalContext();
	const intl = useIntl();
	const navigate = useNavigate();
	const { openSnackbar } = useSnackbar();

	const handleDelete = () => {
		deleteAction(
			{ id },
			{
				onSuccess: (result) => {
					mutationResponseHandler({
						result: result.deleteAction.ok,
						toastMessages: {
							success: intl.formatMessage({ id: 'action.delete.success' }),
							error: intl.formatMessage({ id: 'action.delete.error' }),
						},
						errorMessage: result.deleteAction.errorMessage,
						openSnackbar,
						callback: () => {
							closeModal();
							navigate('/actions');
						},
					});
				},
			},
		);
	};

	return (
		<>
			<ModalContent
				isDelete
				title={intl.formatMessage({ id: 'action.delete.confirmation' }, { name: data?.getAction?.name })}
				description={''}
			>
				<Typography textAlign={'center'}>
					<strong>
						<FormattedMessage id={'common.delete.confirmation'} />
					</strong>
				</Typography>
			</ModalContent>
			<ModalActions
				isDelete
				handleCancelButtonClick={closeModal}
				handlePrimaryButtonClick={handleDelete}
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				primaryButtonText={intl.formatMessage({ id: 'common.delete' })}
			/>
		</>
	);
};
